import { GeocodeResult } from './geocoder';

export type JobImageType = "photo" | "signature";
export type JobSearchSortDirection = "asc" | "desc" | string;

export interface LoadJobsParams {
  limit?: number;
  skip?: number;
  from_ts?: number;
  to_ts?: number;
  time_field?: string;
  children?: boolean;
  state?: string;
}

export interface SearchJobsParams {
  org_ids?: number[];
  query?: string;
  skip_records?: number;
  size: number;

  sort_field?: string;
  sort_direction?: JobSearchSortDirection;

  agent_ids?: number[];
  description?: string;
  destination_name?: string;
  phase?: string;
}

export interface Job {
  _id: number;
  agent_id: number;
  due_time: number;
  description: string;
  destination: {
    name: string;
    lat: number;
    lng: number;
  };
  phase: JobPhase;
  operation: Operation;
  metadata: JobMetadata[];
  start_time: number;
  removed: boolean;
  completed: boolean;
  invite_code?: string;
  created_time: number;
  completed_time?: number
  arrived_time: number;
  chat_channel?: number;
  recipients: Array<{
    name?: string;
    address: string;
    notify?: boolean;
  }>;
  images: Array<{
    url: string;
    agent_id: number;
    created: number;
    lng: number;
    lat: number;
    type: JobImageType;
    description?: string;
  }>;
  feedback?: {
    rating: number;
    comment: string;
  };
  aggregations?: {
    task_id: number;
    location_shared: boolean;
    distance_traveled: number;
  };
  org_id: number;
  foreign_id?: string;
  session_id?: number;
  rank?: number;
}

export interface JobMetadata {
  n: string;
  v: string | { [key: string]: string | number };
}

export enum JobPhase {
  Pre = 'pre',
  Scheduled = 'eta',
  Quasi = 'quasi',
  Live = 'live',
  Arrived = 'arrived',
  Feedback = 'feedback',
  Cancelled = 'cancelled',
  Completed = 'completed',
  Hold = 'not_completed',
}

export const LIVE_MAP_PHASES = [JobPhase.Live, JobPhase.Quasi, JobPhase.Arrived];

export const JobPhaseColor = {
  [JobPhase.Pre]: 'secondary',
  [JobPhase.Scheduled]: 'secondary',
  [JobPhase.Live]: 'info',
  [JobPhase.Quasi]: 'info',
  [JobPhase.Arrived]: 'warning',
  [JobPhase.Feedback]: 'success',
  [JobPhase.Cancelled]: 'danger',
  [JobPhase.Completed] : 'success',
  [JobPhase.Hold]: 'secondary',
};

export const JobPhaseLabel = {
  [JobPhase.Pre]: 'Scheduled',
  [JobPhase.Scheduled]: 'Scheduled',
  [JobPhase.Live]: 'En Route',
  [JobPhase.Quasi]: 'Quasi',
  [JobPhase.Arrived]: 'Arrived',
  [JobPhase.Feedback]: 'Complete',
  [JobPhase.Cancelled]: 'Canceled',
  [JobPhase.Completed]: 'Complete',
  [JobPhase.Hold]: 'Hold',
};

export const JobPhaseFilterMapper = {
  'scheduled': [JobPhase.Pre, JobPhase.Scheduled],
  'en route': [JobPhase.Live],
  'quasi': [JobPhase.Quasi],
  'arrived': [JobPhase.Arrived],
  'complete': [JobPhase.Feedback, JobPhase.Completed],
  'cancelled': [JobPhase.Cancelled],
  'hold': [JobPhase.Hold],
}

export const JobPhaseOrder = {
  [JobPhase.Pre]: JobPhase.Live,
  [JobPhase.Live]: JobPhase.Arrived,
  [JobPhase.Arrived]: null,
};

export interface TransformedJob {
  id: number;
  agent: string;
  description: string;
  destination: string;
  phase: string;
  due_time: string;
  job: Job;
  recipients: string[];
  orgId?: number;
  completed?: boolean;
  completedReasonCode?: string;
}

export interface Operation {
  task_id: number;
  start_time: number;
  invite_code: string;
  invite_url: string;
}

export interface JobPayload {
  appointment: {
    from: Date;
    to: Date;
    id: string;
  };
  destination: GeocodeResult & { showCoordinates: boolean };
  description: string;
  customer: {
    name: string;
    phones: Array<string>;
    emails: Array<string>;
  };
  technician: number;
  recipients?: {
    update: string[];
    add: string[];
  }
}

export interface JobsUploadStatus {
  id: string;
  org_id: string;
  status: UploadStatus;
  created: number;
  updated: number;
  task_data?: {
    processed: number;
    errored: number;
    total: number;
  };
}

export interface TransformedUpload {
  id: string;
  status: UploadStatus;
  created: number;
  updated: number;
  processedRows: number;
  erroredRows: number;
  totalRows: number;
}

export enum UploadStatus {
  Created = 'created',
  Processing = 'processing',
  Done = 'done',
  Error = 'error',
}

export interface JobsUploadError {
  message: string;
  reason: string;
  data: JobsUploadLineError[];
}

export interface JobsUploadLineError {
  line: number;
  error: string;
  errors: {
    [key: string]: string;
  };
}

export interface JobsUploadStatusWithErrors {
  upload: JobsUploadStatus;
  errors: {
    line: number;
    message: string;
  }[];
}

export interface ReassignJobModal {
  show: boolean;
  jobID: number;
  currentTechnician: number;
}

export interface ReassignJobPayload {
  technician: number;
}

export interface EditJobModal {
  show: boolean;
  jobID: number;
}

export interface BulkUploadTaskContainer {
  id: string;
  line: number;
  skip: boolean;
  processed: boolean;
  errors: BulkUploadLineError[];
  task: BulkUploadTask;
  diff?: BulkUpdateDiff;
}

export interface BulkUploadTask {
  ID?: number;
  CustomerName: string;
  Technician: number;
  DestinationAddress: string;
}

export interface BulkUpdateDiff {
  AppointmentID?: string;
  AppointmentFrom?: string;
  AppointmentTo?: string;
  CustomerEmail?: string;
  CustomerPhone?: string;
  Technician?: number;
}

export interface BulkUploadTaskUpdate {
  skip?: boolean;
}

export interface TransformedBulkTask {
  id: string;
  line: number;
  errors: BulkUploadLineError[];
  skip: boolean;
  processed: boolean;
  isUpdating: boolean;
  task: BulkUploadTask;
  hasErrors: boolean;
  customerName: string;
  destination: string;
}

export interface BulkUploadLineError {
  type: string; // TODO: update to be an enum
  error: string;
}
